/**
 * El borrador del asistente: la campaña guardada, lo que se eligió en cada paso y las listas para elegir.
 *
 * - El borrador vive EN EL SERVIDOR (campaña en estado `borrador`): cada paso guarda al avanzar y un F5 retoma desde la
 *   base. Nada de localStorage para el borrador (a diferencia del wizard de Activa, que guarda CSV con datos personales
 *   en el navegador).
 * - Al cargar: lo guardado vuelve a lo elegido (`aplicarCampana`), el paso más lejano que permite (`maxPasoAlcanzable`,
 *   que fija `setMaxAlcanzado` y corrige `?paso=` con `irA`) y la prueba que ya salió, de la foto del viaje al editor o
 *   de la auditoría de la campaña (`pruebaDeLaAuditoria`): así un F5 en «Revisar y mandar» no pide otra.
 * - Lo elegido es una sola fuente para todos los pasos (y para el panel «Tu campaña», que muestra cómo va): la plantilla,
 *   la audiencia de cada opción, el horario y la baja. El nombre es el guardado: se cambia en el panel.
 * - Lo que sale es lo guardado (29/10/2026): «Cuándo» y «Revisar y mandar» usan la plantilla, el horario y la baja que la
 *   campaña tiene guardados (`detalleGuardada`, `horarioGuardado`, `bajaGuardada`), no lo elegido en pantalla. Antes,
 *   con otra plantilla elegida y sin guardar, la pantalla decía una y salía otra.
 * - Cada `PATCH` pasa por `registrarGuardado`: la campaña como quedó y hasta qué paso deja llegar, que puede bajar (el
 *   `PATCH` pudo descartar algo, como las variables al cambiar la plantilla).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import type {
  AudienciaActiva, CampanaDetalle, CampanaSinWhatsapp, CampoDisponible, EstadoPlantilla, ExcluirAudiencia, FiltroAudienciaPublico, FiltroCompras,
  ModoTransporte, PlantillaDetalle, PlantillaResumen, ResultadoPrueba,
} from "../../types";
import type { PublicoResumen } from "../../types.base";
import { oficialApi } from "../../api";
import { baseApi } from "../../baseApi";
import type { PasoAsistente } from "../../logic/chequeo";
import { maxPasoAlcanzable, pruebaDeLaAuditoria } from "../../logic/chequeo";
import { camposValidosGuardados } from "../../logic/mapeo";
import { variablesSinClics } from "../../logic/clics";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { FILTRO_PUBLICO_VACIO } from "../../components/AudienciaPublico";
import { FILTRO_COMPRAS_VACIO, filtroComprasDe } from "../../logic/compras";
import { EXCLUIR_VACIO, excluirDeAudiencia } from "../../logic/exclusiones";
import { HORARIO_VACIO, horarioEnEdicion, type HorarioEnEdicion } from "../../logic/horario";
import { eleccionDeBaja, type EleccionBaja } from "../../logic/baja";
import { esInutilizable, idx, pasoDeLaUrl, queCambio, sacarFoto, type CambioGuardado, type TipoPestana } from "../../logic/asistente";
import type { PruebaCampana } from "./usePruebaCampana";

const B = "/activa/oficial";

export function useBorradorCampana({ idRuta, sp, modo, publicoUrl, irA, setMaxAlcanzado, prueba }: {
  idRuta: string | undefined;
  sp: URLSearchParams;
  modo: ModoTransporte | null;
  /** `?publico=`: el público de la base con el que se llegó (R-B12) */
  publicoUrl: string;
  irA: (p: PasoAsistente, reemplazar?: boolean) => void;
  setMaxAlcanzado: Dispatch<SetStateAction<number>>;
  prueba: Pick<PruebaCampana, "restaurar" | "retomar" | "setSinPrueba">;
}) {
  const navigate = useNavigate();

  // ── Datos ────────────────────────────────────────────────────────────────
  const [campana, setCampana] = useState<CampanaDetalle | null>(null);
  const [cargandoCampana, setCargandoCampana] = useState<boolean>(!!idRuta);
  const [errorCarga, setErrorCarga] = useState("");
  const [noEncontrada, setNoEncontrada] = useState(false);
  const [plantillas, setPlantillas] = useState<PlantillaResumen[] | null>(null);
  const [errorPlantillas, setErrorPlantillas] = useState("");
  const [audiencias, setAudiencias] = useState<AudienciaActiva[] | null>(null);
  const [errorAudiencias, setErrorAudiencias] = useState("");
  // las campañas de Activa preparadas con todo por mail: no están en `audiencias`, y el paso dice cuáles y cómo pasarlas
  const [sinWhatsapp, setSinWhatsapp] = useState<CampanaSinWhatsapp[]>([]);
  const [campos, setCampos] = useState<CampoDisponible[]>([]);
  const [detalles, setDetalles] = useState<Record<string, PlantillaDetalle>>({});
  const [errorDetalle, setErrorDetalle] = useState("");
  const [guardando, setGuardando] = useState(false);

  // ── Lo elegido: plantilla y audiencia ────────────────────────────────────
  const [plantillaId, setPlantillaId] = useState(sp.get("plantilla") || "");
  // `?analisis=` llega de «Armar en el oficial» (Mensajes, fase 4): esa campaña de Activa ya elegida
  const [audienciaId, setAudienciaId] = useState(() => sp.get("analisis") || "");
  const [modoGrupos, setModoGrupos] = useState<"todos" | "elegir">("todos");
  const [elegidos, setElegidos] = useState<string[]>([]);
  // Sin grupo de control: WhatsApp oficial le escribe a todos los incluidos (el backend rechaza un
  // `controlPct` > 0 con 422 CONTROL_NO_DISPONIBLE), así que el asistente ni lo pide ni lo manda,
  // tampoco con un público de la base.
  const publicoUrlRef = useRef(publicoUrl);
  publicoUrlRef.current = publicoUrl;
  const [publicoId, setPublicoId] = useState(publicoUrl);
  const [publicos, setPublicos] = useState<PublicoResumen[] | null>(null);
  const [errorPublicos, setErrorPublicos] = useState("");
  /** El Público de Activa como audiencia: segmento, actividad (vacíos = todos) y, si se subió, la lista de un archivo. */
  const [filtroPublico, setFiltroPublico] = useState<FiltroAudienciaPublico>(FILTRO_PUBLICO_VACIO);
  /** El filtro «Por compras» (28/10/2026): de la audiencia del Público o de la Base, sólo los que cumplen lo de sus compras. */
  const [filtroCompras, setFiltroCompras] = useState<FiltroCompras>(FILTRO_COMPRAS_VACIO);
  /**
   * Las exclusiones elegidas (28/10/2026): sin quien recibió otras campañas o está en una lista. Con cualquier audiencia
   * desde el 29/10/2026: cambiar de pestaña las conserva (es un solo estado y se guarda con cualquier tipo).
   */
  const [excluir, setExcluir] = useState<ExcluirAudiencia>(EXCLUIR_VACIO);
  /** De qué tipo de audiencia son los `campos` cargados (null mientras llegan). */
  const [camposDe, setCamposDe] = useState<TipoPestana | null>(null);
  const [camposFallaron, setCamposFallaron] = useState(false);

  // ── Lo elegido: horario y baja ───────────────────────────────────────────
  const [horarioEd, setHorarioEd] = useState<HorarioEnEdicion>(HORARIO_VACIO);
  // «Cuándo» (antes el paso «Horario y baja», 26/10): si ya se eligió la franja (la de la marca o una propia) y la baja.
  const [horarioElegido, setHorarioElegido] = useState(false);
  const [eleccionBaja, setEleccionBaja] = useState<EleccionBaja | "">("");

  const idCampana = campana?.id || "";
  // Lo último que se sabe de la campaña, para lo que sigue después de un `await` (duplicar una
  // plantilla, el estado en vivo): un cierre del render anterior vería la campaña vieja.
  const campanaRef = useRef<CampanaDetalle | null>(campana);
  campanaRef.current = campana;
  const detalle = plantillaId ? detalles[plantillaId] : undefined;
  const plantillaResumen = plantillas?.find((p) => p.id === plantillaId);
  // Estado de la plantilla elegida: el detalle (lo actualiza el estado en vivo), la lista, o lo que trajo la campaña.
  const estadoElegida: EstadoPlantilla | undefined = detalle?.estado ?? plantillaResumen?.estado
    ?? (campana?.plantilla?.id === plantillaId ? campana?.plantilla?.estado : undefined);
  // Simulada o de otra cuenta: manda el detalle (el más nuevo) y, si no llegó, la lista.
  const elegidaInutilizable = !!plantillaId && esInutilizable({
    simulado: detalle?.simulado ?? plantillaResumen?.simulado,
    deOtraCuenta: detalle?.deOtraCuenta ?? plantillaResumen?.deOtraCuenta,
  }, modo);
  // Archivada: lo dice el detalle (la lista viene sin las archivadas, así que la de la lista nunca lo decía).
  const elegidaArchivada = !!(detalle?.archivada ?? plantillaResumen?.archivada);
  // La plantilla guardada en la campaña, que es la que sale: la que usan «Cuándo» y «Revisar y mandar».
  const plantillaGuardadaId = campana?.plantilla?.id || "";
  const detalleGuardada = plantillaGuardadaId ? detalles[plantillaGuardadaId] : undefined;
  const resumenGuardada = plantillaGuardadaId ? plantillas?.find((p) => p.id === plantillaGuardadaId) : undefined;
  const estadoGuardada: EstadoPlantilla | undefined = detalleGuardada?.estado ?? resumenGuardada?.estado ?? campana?.plantilla?.estado;
  // El horario y la baja guardados, como los muestra «Cuándo»: lo que repasa y manda «Revisar y mandar».
  const horarioGuardado = useMemo(() => horarioEnEdicion(campana?.horario), [campana?.horario]);
  const bajaGuardada = eleccionDeBaja(campana?.baja);

  // Tipo de audiencia guardado: decide qué campos existen para las variables (SPEC-BD D8).
  const tipoGuardado: TipoPestana = campana?.audiencia?.tipo === "base" ? "base" : campana?.audiencia?.tipo === "publico" ? "publico" : "activa";
  const camposListos = camposDe === tipoGuardado;
  const camposValidos = useMemo<string[]>(
    () => (camposListos && !camposFallaron ? campos.map((c) => String(c.campo)) : camposValidosGuardados(tipoGuardado, campana?.mapeo)),
    [camposListos, camposFallaron, campos, tipoGuardado, campana?.mapeo],
  );

  // ── Carga de listas ──────────────────────────────────────────────────────
  const cargarPlantillas = useCallback(async () => {
    try {
      const l = leerRespuesta<{ items: PlantillaResumen[] }>(await oficialApi.plantillas());
      if (!l.ok) { setErrorPlantillas(l.error || textoError(l)); return; }
      setPlantillas(l.data?.items || []);
      setErrorPlantillas("");
    } catch (e) { setErrorPlantillas(textoError(leerError(e))); }
  }, []);
  const cargarAudiencias = useCallback(async () => {
    try {
      const l = leerRespuesta<AudienciaActiva[]>(await oficialApi.audiencias());
      if (!l.ok) { setErrorAudiencias(l.error || textoError(l)); return; }
      setAudiencias(Array.isArray(l.data) ? l.data : []);
      setErrorAudiencias("");
    } catch (e) { setErrorAudiencias(textoError(leerError(e))); }
    // aparte y sin error a la vista: sin este dato (o con un backend anterior) queda el aviso de siempre
    try {
      const s = leerRespuesta<CampanaSinWhatsapp[]>(await oficialApi.campanasSinWhatsapp());
      setSinWhatsapp(s.ok && Array.isArray(s.data) ? s.data : []);
    } catch { setSinWhatsapp([]); }
  }, []);
  const cargarPublicos = useCallback(async () => {
    setErrorPublicos("");
    try {
      const l = leerRespuesta<PublicoResumen[]>(await baseApi.publicos());
      if (!l.ok) { setErrorPublicos(l.error || textoError(l)); return; }
      setPublicos(Array.isArray(l.data) ? l.data : []);
    } catch (e) { setErrorPublicos(textoError(leerError(e))); }
  }, []);
  /** Detalle de una plantilla (variables y texto para la previa), con caché por id. */
  const cargarDetalle = useCallback(async (pid: string): Promise<PlantillaDetalle | null> => {
    try {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.plantilla(pid));
      if (!l.ok || !l.data) { setErrorDetalle(l.ok ? "No pudimos traer la plantilla." : l.error || textoError(l)); return null; }
      setDetalles((d) => ({ ...d, [pid]: l.data }));
      setErrorDetalle("");
      return l.data;
    } catch (e) {
      setErrorDetalle(`No pudimos traer la plantilla. ${textoError(leerError(e))}`);
      return null;
    }
  }, []);

  useEffect(() => {
    void cargarPlantillas();
    void cargarAudiencias();
    void cargarPublicos();
  }, [cargarPlantillas, cargarAudiencias, cargarPublicos]);

  // Los campos dependen de la audiencia guardada: con un público de la base suman los personalizados.
  useEffect(() => {
    let vivo = true;
    setCamposDe(null);
    (async () => {
      try {
        const l = leerRespuesta<CampoDisponible[]>(await oficialApi.campos(tipoGuardado));
        if (!vivo) return;
        if (l.ok && Array.isArray(l.data)) { setCampos(l.data); setCamposFallaron(false); }
        else { setCampos([]); setCamposFallaron(true); }
      } catch {
        // Sin campos el formulario muestra el nombre crudo del campo, y la validación usa lo guardado.
        if (vivo) { setCampos([]); setCamposFallaron(true); }
      } finally {
        if (vivo) setCamposDe(tipoGuardado);
      }
    })();
    return () => { vivo = false; };
  }, [tipoGuardado]);

  // Precarga de la plantilla elegida (su previa y sus variables, en «Qué les decís») y de la guardada («Cuándo» y
  // «Revisar y mandar» muestran ésa).
  useEffect(() => {
    if (plantillaId && !detalles[plantillaId]) void cargarDetalle(plantillaId);
  }, [plantillaId, detalles, cargarDetalle]);
  useEffect(() => {
    // la misma que la elegida la pide el efecto de arriba
    if (plantillaGuardadaId && plantillaGuardadaId !== plantillaId && !detalles[plantillaGuardadaId]) void cargarDetalle(plantillaGuardadaId);
  }, [plantillaGuardadaId, plantillaId, detalles, cargarDetalle]);

  // ── Carga de la campaña (editar) ─────────────────────────────────────────
  /** La audiencia guardada, de vuelta en lo elegido. `conPublicoUrl`: un `?publico=` manda sobre el público guardado. */
  const aplicarAudiencia = useCallback((c: CampanaDetalle, conPublicoUrl: boolean) => {
    if (!c.audiencia) return;
    // Un `?publico=` recién creado desde el asistente («Guardar y volver a la campaña») manda sobre el guardado.
    if (c.audiencia.tipo === "base") setPublicoId((conPublicoUrl && publicoUrlRef.current) || c.audiencia.publicoId || "");
    if (c.audiencia.tipo === "publico") setFiltroPublico({ ...FILTRO_PUBLICO_VACIO, ...(c.audiencia.filtroPublico || {}) });
    // el filtro «Por compras» guardado (sólo en el Público y la Base): el bloque lo vuelve a mostrar
    if (c.audiencia.tipo === "publico" || c.audiencia.tipo === "base") setFiltroCompras(filtroComprasDe(c.audiencia.filtroCompras));
    // y las exclusiones guardadas (con cualquier audiencia desde el 29/10/2026): el bloque «Excluir» las vuelve a marcar
    setExcluir(excluirDeAudiencia(c.audiencia.excluir));
    setAudienciaId(c.audiencia.campanaActivaId);
    // `grupos: []` significa TODOS para el backend (SPEC-backend §5.3).
    if (c.audiencia.grupos?.length) { setModoGrupos("elegir"); setElegidos(c.audiencia.grupos); }
    else { setModoGrupos("todos"); setElegidos([]); }
  }, []);
  /** El horario y la baja guardados, de vuelta en lo elegido en «Cuándo». */
  const aplicarHorario = useCallback((c: CampanaDetalle) => {
    // Una campaña de antes del paso (sin los datos) arranca con la franja de la marca ya elegida y la baja por elegir:
    // así sale como siempre, pero el paso pide decidir cómo se dan de baja.
    setHorarioEd(horarioEnEdicion(c.horario));
    setHorarioElegido(c.horarioElegido !== false);
    setEleccionBaja(eleccionDeBaja(c.baja));
  }, []);
  const aplicarCampana = useCallback((c: CampanaDetalle) => {
    setCampana(c);
    if (c.plantilla?.id) setPlantillaId(c.plantilla.id);
    aplicarAudiencia(c, true);
    aplicarHorario(c);
  }, [aplicarAudiencia, aplicarHorario]);
  /** «Descartar» los cambios de «A quién» o de «Cuándo» (`CambiosDelPaso`): lo elegido vuelve a lo guardado. */
  const descartarAudiencia = useCallback(() => { if (campanaRef.current) aplicarAudiencia(campanaRef.current, false); }, [aplicarAudiencia]);
  const descartarHorario = useCallback(() => { if (campanaRef.current) aplicarHorario(campanaRef.current); }, [aplicarHorario]);

  /**
   * Hasta qué paso deja llegar lo que la campaña tiene guardado (un índice de `PASOS`): `maxPasoAlcanzable` con las
   * variables de su plantilla. Sin el detalle de la plantilla, como mucho el mensaje (fail-closed, como al cargar). Los
   * campos de `GET /campos` valen si son del tipo de audiencia de `c`; si no (la audiencia cambió de tipo), los que el
   * mapeo guardado ya usa.
   */
  const detallesRef = useRef(detalles);
  detallesRef.current = detalles;
  const camposDeRef = useRef<{ tipo: TipoPestana; validos: string[] | undefined }>({ tipo: tipoGuardado, validos: undefined });
  camposDeRef.current = { tipo: tipoGuardado, validos: camposListos && !camposFallaron ? camposValidos : undefined };
  const alcanceDe = useCallback((c: CampanaDetalle): number => {
    const det = c.plantilla?.id ? detallesRef.current[c.plantilla.id] : undefined;
    const tipo: TipoPestana = c.audiencia?.tipo === "base" ? "base" : c.audiencia?.tipo === "publico" ? "publico" : "activa";
    const campos = camposDeRef.current.tipo === tipo ? camposDeRef.current.validos : undefined;
    const nums = variablesSinClics(det?.variables, det?.botones).map((v) => v.numero);
    const n = idx(maxPasoAlcanzable(c, nums, { plantillaInutilizable: esInutilizable(det, modo), plantillaArchivada: !!det?.archivada, camposValidos: campos }));
    return c.plantilla && !det ? Math.min(n, idx("mensaje")) : n;
  }, [modo]);
  /**
   * Lo que devolvió un `PATCH`: la campaña como quedó y hasta qué paso deja llegar ahora (`alcanceDe`), que puede bajar.
   * `aplicar` (lo de siempre) vuelve lo guardado a lo elegido (`aplicarCampana`); sin él, sólo la campaña (las variables y
   * el nombre, que tienen su propio estado). Devuelve el alcance, para no ir más lejos.
   */
  const registrarGuardado = useCallback((c: CampanaDetalle, aplicar = true): number => {
    if (aplicar) aplicarCampana(c);
    else setCampana(c);
    campanaRef.current = c;
    const n = alcanceDe(c);
    setMaxAlcanzado(n);
    return n;
  }, [aplicarCampana, alcanceDe, setMaxAlcanzado]);
  /**
   * Otra pestaña pudo cambiar la campaña: la vuelve a traer (`GET /campanas/:id`) y dice qué cambió de lo guardado
   * (`queCambio`). Con algo distinto, lo guardado nuevo pasa a la pantalla (`registrarGuardado`). Una que ya no es
   * borrador (se confirmó en otra pestaña) vuelve con `confirmada`. null si no se pudo traer: queda lo de antes.
   */
  const releer = useCallback(async (): Promise<{ campana: CampanaDetalle; cambios: CambioGuardado[]; confirmada: boolean } | null> => {
    const antes = campanaRef.current;
    if (!antes) return null;
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.campana(antes.id));
      if (!l.ok || !l.data || campanaRef.current?.id !== antes.id) return null;
      const c = l.data;
      if (c.estado !== "borrador") return { campana: c, cambios: [], confirmada: true };
      const cambios = queCambio(campanaRef.current ?? antes, c);
      // con otra plantilla, hasta dónde se llega sale de sus variables (sin ellas, como mucho el mensaje): se trae antes
      const pid = c.plantilla?.id;
      if (cambios.includes("plantilla") && pid && !detallesRef.current[pid]) {
        const det = await cargarDetalle(pid);
        if (det) detallesRef.current = { ...detallesRef.current, [pid]: det };
      }
      if (cambios.length) registrarGuardado(c);
      return { campana: c, cambios, confirmada: false };
    } catch {
      return null;
    }
  }, [registrarGuardado, cargarDetalle]);

  useEffect(() => {
    if (!idRuta) { setCargandoCampana(false); return; }
    let vivo = true;
    setCargandoCampana(true);
    setErrorCarga("");
    setNoEncontrada(false);
    (async () => {
      try {
        const l = leerRespuesta<CampanaDetalle>(await oficialApi.campana(idRuta));
        if (!vivo) return;
        if (!l.ok) { setErrorCarga(l.error || textoError(l)); return; }
        const c = l.data;
        if (c.estado !== "borrador") { navigate(`${B}/campanas/${encodeURIComponent(c.id)}`, { replace: true }); return; }
        let nums: number[] = [];
        let detalleOk = true;
        let inutilizable = false;
        let archivada = false;
        if (c.plantilla?.id) {
          const det = await cargarDetalle(c.plantilla.id);
          if (!vivo) return;
          detalleOk = !!det;
          // sin la del botón que cuenta clics: lleva un código propio por mensaje y no se mapea
          nums = variablesSinClics(det?.variables, det?.botones).map((v) => v.numero);
          inutilizable = esInutilizable(det, modo);
          archivada = !!det?.archivada;
        }
        let max = maxPasoAlcanzable(c, nums, { plantillaInutilizable: inutilizable, plantillaArchivada: archivada });
        // Sin las variables de la plantilla no se puede saber si el mapeo está completo: se para en el mensaje (fail-closed).
        if (!detalleOk && idx(max) > idx("mensaje")) max = "mensaje";
        aplicarCampana(c);
        // Con campaña, `?plantilla=` sólo llega de la página del editor (`rutaVolverAlAsistente`): la
        // plantilla con la que se volvió queda elegida aunque no se haya podido asociar y la campaña siga
        // con la anterior (el error de la asociación viene en `location.state`).
        const pUrl = sp.get("plantilla");
        const foto = sacarFoto(c.id);
        let conFoto: ResultadoPrueba | null = null;
        if (pUrl) setPlantillaId(pUrl);
        else if (foto && foto.asociada === (c.plantilla?.id || "")) {
          // Volvió del editor sin que se asociara otra plantilla: lo que había elegido y probado sigue valiendo.
          if (foto.plantillaId) setPlantillaId(foto.plantillaId);
          prueba.setSinPrueba(foto.sinPrueba);
          if (foto.prueba) { prueba.restaurar(foto.prueba, foto.pruebaInicio); conFoto = foto.prueba; }
        }
        // Un F5 (o volver otro día): la prueba que ya salió, y se cobró, está en la auditoría. Antes vivía sólo en
        // memoria y un F5 en Confirmar pedía otra (USD 0,08 cada una). Vale si es de la plantilla de ahora (cambiar de
        // plantilla la invalida, como en memoria: `pruebaDeLaAuditoria`).
        const guardada = conFoto ? null : pruebaDeLaAuditoria(c);
        if (guardada) {
          // con el estado de AHORA (`retomar`): el de la auditoría es el de cuando salió
          await prueba.retomar(guardada);
          if (!vivo) return;
        }
        setMaxAlcanzado(idx(max));
        // Un `?paso=` inalcanzable (o ausente) se corrige en la URL, sin sumar una entrada al historial. Uno viejo
        // (`?paso=confirmar`) vale como el nuevo: lo traduce `pasoDeLaUrl`, y la URL la corrige `usePasoEnUrl`.
        const p = pasoDeLaUrl(sp.get("paso"));
        if (!p || idx(p) > idx(max)) irA(max, true);
      } catch (e) {
        if (!vivo) return;
        const x = leerError(e);
        if (x.status === 404) setNoEncontrada(true);
        else setErrorCarga(textoError(x));
      } finally {
        if (vivo) setCargandoCampana(false);
      }
    })();
    return () => { vivo = false; };
    // Sólo cuando cambia el id: el resto son funciones estables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idRuta]);

  return {
    campana, setCampana, campanaRef, idCampana, cargandoCampana, errorCarga, noEncontrada, aplicarCampana, registrarGuardado, releer, guardando, setGuardando,
    plantillas, setPlantillas, errorPlantillas, cargarPlantillas, detalles, setDetalles, detalle, errorDetalle,
    plantillaResumen, estadoElegida, elegidaInutilizable, elegidaArchivada, detalleGuardada, resumenGuardada, estadoGuardada,
    horarioGuardado, bajaGuardada, descartarAudiencia, descartarHorario,
    audiencias, errorAudiencias, sinWhatsapp, cargarAudiencias, publicos, setPublicos, errorPublicos, cargarPublicos,
    campos, camposListos, camposFallaron, camposValidos, tipoGuardado,
    plantillaId, setPlantillaId,
    audienciaId, setAudienciaId, modoGrupos, setModoGrupos, elegidos, setElegidos, publicoId, setPublicoId,
    filtroPublico, setFiltroPublico, filtroCompras, setFiltroCompras, excluir, setExcluir,
    horarioEd, setHorarioEd, horarioElegido, setHorarioElegido, eleccionBaja, setEleccionBaja,
  };
}

export type BorradorCampana = ReturnType<typeof useBorradorCampana>;
